import { formatRiskPointChange } from "@/revenue-intelligence/lib/risk-contracts";
import { Database } from "@revelate/types";
import { format } from "date-fns";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import { getRiskBucket } from "../lib/risk";
import {
  getDealForecastContribution,
  type ForecastInsightDeal,
  type ForecastL2Insights,
  type ForecastManagementAction,
  type ForecastMovementCategoryInsight,
} from "./forecastInsights";

type Currency = Database["public"]["Tables"]["currencies"]["Row"] | null;

export type ForecastSectionCopy = {
  heading: string;
  label: string;
  supporting?: string;
};

export type ForecastExecutiveCopy = {
  conclusion: string;
  evidence?: string;
  explanation?: string;
};

export function formatForecastShare(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
    style: "percent",
  }).format(value);
}

function formatForecastDate(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "d MMM yyyy");
}

function joinNames(names: string[]) {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

function getPositionHeading(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
) {
  const { position } = insights;
  const subject = position.historical ? "Won" : "Forecast";
  if (position.status === "target_unavailable") {
    return "Target is unavailable";
  }
  if (position.status === "on_target") return `${subject} is on target`;
  const difference = formatCompactIsoCurrency(position.difference, companyCurrency);
  return position.status === "above_target"
    ? `${subject} is ${difference} above target`
    : `${subject} is ${difference} below target`;
}

function getMovementHeading(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
) {
  const movement = insights.movement;
  if (movement.state === "insufficient_history") {
    return "Movement history is unavailable";
  }
  if (movement.direction === "stable") return "Forecast was unchanged";
  return `Forecast ${movement.direction === "increase" ? "increased" : "decreased"} by ${formatCompactIsoCurrency(movement.amount, companyCurrency)}`;
}

function getMovementSupporting(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
) {
  const movement = insights.movement;
  if (movement.state === "insufficient_history") {
    return "Comparable 30-day Forecast history is unavailable.";
  }
  if (movement.direction === "stable" && movement.positiveMovement === 0 && movement.negativeMovement === 0) {
    return "No movement in the last 30 days.";
  }
  return `${formatCompactIsoCurrency(movement.positiveMovement, companyCurrency)} added; ${formatCompactIsoCurrency(movement.negativeMovement, companyCurrency)} removed in 30 days.`;
}

export function getForecastExecutiveCopy(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
): ForecastExecutiveCopy {
  const positionHeading = getPositionHeading(insights, companyCurrency);
  const movement = insights.movement;
  if (insights.position.historical) {
    return { conclusion: `${positionHeading}.` };
  }
  const movementClause = movement.state === "insufficient_history"
    ? ""
    : movement.direction === "stable"
      ? " Forecast was unchanged in 30 days."
      : ` Forecast was ${movement.direction === "increase" ? "up" : "down"} ${formatCompactIsoCurrency(movement.amount, companyCurrency)} in 30 days.`;
  const explanation = movement.state === "insufficient_history"
    ? "30-day movement is unavailable."
    : movement.direction === "stable"
      ? "No material 30-day movement."
      : `${formatCompactIsoCurrency(movement.positiveMovement, companyCurrency)} added; ${formatCompactIsoCurrency(movement.negativeMovement, companyCurrency)} removed over 30 days.`;
  const driver = movement.dominantDriver;
  const driverNames = driver?.deals.slice(0, 2).map(deal => deal.name).filter(Boolean) ?? [];
  const evidence = driver
    ? driverNames.length
      ? `${driver.label} was the largest movement, led by ${joinNames(driverNames)}.`
      : `${driver.label} was the largest movement.`
    : undefined;

  const integratedConclusion = insights.position.status === "target_unavailable"
    ? `${positionHeading}.${movementClause}`
    : insights.position.status === "on_target"
      ? movement.direction === "stable" || movement.state === "insufficient_history"
        ? "Forecast is on target."
        : `Forecast is on target, ${movement.direction === "increase" ? "up" : "down"} ${formatCompactIsoCurrency(movement.amount, companyCurrency)} in 30 days.`
      : movement.state === "insufficient_history"
        ? `${positionHeading}.`
        : `Forecast is ${formatCompactIsoCurrency(insights.position.difference, companyCurrency)} ${insights.position.status === "above_target" ? "above" : "below"} target, ${movement.direction === "stable" ? "unchanged" : movement.direction === "increase" ? "up" : "down"}${movement.direction === "stable" ? "" : ` ${formatCompactIsoCurrency(movement.amount, companyCurrency)}`} in 30 days.`;

  return {
    conclusion: integratedConclusion.replace("..", "."),
    evidence,
    explanation,
  };
}

export function getCurrentPositionCopy(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
): ForecastSectionCopy {
  return {
    heading: getPositionHeading(insights, companyCurrency),
    label: "Current position",
  };
}

export function getForecastMovementCopy(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
): ForecastSectionCopy {
  return {
    heading: getMovementHeading(insights, companyCurrency),
    label: "What changed",
    supporting: getMovementSupporting(insights, companyCurrency),
  };
}

function getCategoryDealNames(category: ForecastMovementCategoryInsight) {
  return category.deals.slice(0, 2).map(deal => deal.name).filter(Boolean);
}

export function getForecastMovementDriverCopy(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
): ForecastSectionCopy {
  const movement = insights.movement;
  if (movement.state === "insufficient_history") {
    return {
      heading: "Movement drivers are unavailable",
      label: "Why it changed",
      supporting: "Comparable v2 history is required before movement drivers can be attributed.",
    };
  }
  const positive = movement.dominantPositiveDriver;
  const negative = movement.dominantNegativeDriver;

  if (positive && negative) {
    return {
      heading: `${positive.label} led gains; ${negative.label} led losses`,
      label: "Why it changed",
      supporting: `${formatCompactIsoCurrency(positive.amount, companyCurrency)} added; ${formatCompactIsoCurrency(Math.abs(negative.amount), companyCurrency)} removed.`,
    };
  }

  const category = positive ?? negative;

  if (!category) {
    if (movement.dominantDriverState === "multiple_equal_drivers") {
      return {
        heading: "Several movements had equal impact",
        label: "Why it changed",
        supporting: "Select a category to view its deals.",
      };
    }
    if (movement.dominantDriverState === "driver_data_unavailable") {
      return {
        heading: "The largest movement is unclassified",
        label: "Why it changed",
        supporting: "Deal history does not fully explain it.",
      };
    }
    return {
      heading: "No movement driver identified",
      label: "Why it changed",
      supporting: "No category changed Forecast.",
    };
  }

  const direction = category.amount > 0 ? "positive" : category.amount < 0 ? "negative" : "neutral";
  const names = getCategoryDealNames(category);
  const heading = `${category.label} was the largest ${direction} driver`;
  const supporting = category.dealCount === 0
    ? "No deal details available."
    : category.amount === 0
      ? `${category.dealCount} ${category.dealCount === 1 ? "deal" : "deals"}; no net Forecast impact.`
      : names.length
        ? `${joinNames(names)} ${category.amount > 0 ? "increased" : "reduced"} Forecast by ${formatCompactIsoCurrency(Math.abs(category.amount), companyCurrency)}.`
        : `${category.dealCount} ${category.dealCount === 1 ? "deal" : "deals"} ${category.amount > 0 ? "increased" : "reduced"} Forecast by ${formatCompactIsoCurrency(Math.abs(category.amount), companyCurrency)}.`;

  return { heading, label: "Why it changed", supporting };
}

export function getForecastMovementCategoryCopy(
  category: ForecastMovementCategoryInsight,
  companyCurrency: Currency,
): ForecastSectionCopy {
  const positive = category.amount > 0;
  const names = getCategoryDealNames(category);
  const supporting = category.dealCount === 0
    ? "No deal details available."
    : names.length
      ? `${joinNames(names)} ${positive ? "increased" : "reduced"} Forecast.`
      : `${category.dealCount} ${category.dealCount === 1 ? "deal" : "deals"} ${positive ? "increased" : "reduced"} Forecast.`;

  return {
    heading: `${category.label} · ${formatCompactIsoCurrency(Math.abs(category.amount), companyCurrency)}`,
    label: positive ? "Largest positive driver" : "Largest negative driver",
    supporting,
  };
}

function getCurrentSignalCopy<T extends ForecastInsightDeal>(
  insights: ForecastL2Insights<T>,
  companyCurrency: Currency,
): ForecastSectionCopy {
  const dominant = insights.currentSignals.dominant;
  if (!dominant) {
    return {
      heading: "No current risk signals",
      label: "Signals to monitor",
    };
  }

  if (insights.currentSignals.state === "several_equal_exposures") {
    const equallyExposed = insights.currentSignals.rows.filter(
      row => row.dealValue === dominant.dealValue,
    );
    return {
      heading: "Several risk signals have equal exposure",
      label: "Signals to monitor",
      supporting: `${joinNames(equallyExposed.slice(0, 2).map(row => row.label))}: ${formatCompactIsoCurrency(dominant.dealValue, companyCurrency)} in deal value each.`,
    };
  }

  return {
    heading: `${dominant.label} is the leading risk signal`,
    label: "Signals to monitor",
    supporting: `${formatCompactIsoCurrency(dominant.dealValue, companyCurrency)} in deal value · ${dominant.affectedDealCount} ${dominant.affectedDealCount === 1 ? "open deal" : "open deals"}.`,
  };
}

export function getForecastSignalsCopy<T extends ForecastInsightDeal>(
  insights: ForecastL2Insights<T>,
  companyCurrency: Currency,
  tab: "current" | "losses",
): ForecastSectionCopy {
  if (tab === "current") {
    return getCurrentSignalCopy(insights, companyCurrency);
  }
  const dominant = insights.lossPatterns.dominant;
  if (!dominant) {
    if (insights.lossPatterns.state === "insufficient_signal_history") {
      return {
        heading: "Signal history is unavailable for recent losses",
        label: "Signals to monitor",
      };
    }
    if (insights.lossPatterns.state === "no_significant_signal") {
      return {
        heading: "No significant signals in recent losses",
        label: "Signals to monitor",
      };
    }
    return {
      heading: "No recent lost deals",
      label: "Signals to monitor",
    };
  }
  if (insights.lossPatterns.sameDealPopulation) {
    const populated = insights.lossPatterns.rows.filter(row => row.affectedDealCount > 0);
    const dealCount = dominant.affectedDealCount;
    return {
      heading: dealCount === 1
        ? "The same recent loss showed several risk signals"
        : "The same recent losses showed several risk signals",
      label: "Signals to monitor",
      supporting: dealCount === 1
        ? `${joinNames(populated.map(row => row.label))} appeared in the same recent lost deal.`
        : `${joinNames(populated.map(row => row.label))} appeared in the same ${dealCount} recent lost deals.`,
    };
  }
  if (insights.lossPatterns.state === "several_equal_patterns") {
    const equallyCommon = insights.lossPatterns.rows.filter(
      row => row.affectedDealCount === dominant.affectedDealCount,
    );
    return {
      heading: "Several risk signals shown in recent losses",
      label: "Signals to monitor",
      supporting: `${joinNames(equallyCommon.slice(0, 2).map(row => row.label))} each appeared in ${dominant.affectedDealCount} of ${dominant.recentLostDealCount} recent lost deals.`,
    };
  }
  const comparison = `${dominant.affectedDealCount} of ${dominant.recentLostDealCount} recent lost deals; ${dominant.openDealCount} of ${dominant.openDealTotal} selected-period open deals.`;
  return {
    heading: `${dominant.label} was the most common risk signal in recent losses`,
    label: "Signals to monitor",
    supporting: comparison,
  };
}

export function getForecastManagementCopy(
  insights: ForecastL2Insights,
  companyCurrency: Currency,
): ForecastSectionCopy {
  const actions = insights.managementAttention.actions;
  if (!actions.length) {
    return {
      heading: "No immediate management priority identified",
      label: "Management attention",
    };
  }
  const leading = actions[0];
  if (!leading) {
    return {
      heading: "No immediate management priority identified",
      label: "Management attention",
    };
  }
  const share = formatForecastShare(leading.shareOfForecast);
  const name = leading.dealName ?? "one deal";
  const resultingGap = leading.riskThesis.consequence.resultingGapToTarget;

  if (leading.type === "forecast_dependency") {
    const forecastWithoutDeal = leading.riskThesis.consequence.forecastWithoutEntity;
    if (resultingGap !== undefined && resultingGap < 0) {
      return {
        heading: `Forecast falls below target without ${name}`,
        label: "Management attention",
        supporting: `${name} contributes ${share} of Forecast. Without the deal, Forecast is ${formatCompactIsoCurrency(Math.abs(resultingGap), companyCurrency)} below target.`,
      };
    }
    return {
      heading: `Forecast depends on ${name}`,
      label: "Management attention",
      supporting: forecastWithoutDeal === undefined
        ? `${name} contributes ${share} of Forecast.`
        : `${name} contributes ${share} of Forecast. Without the deal, Forecast is ${formatCompactIsoCurrency(forecastWithoutDeal, companyCurrency)}.`,
    };
  }

  if (leading.type === "worsening_outlook") {
    return {
      heading: `Forecast weakened by ${formatCompactIsoCurrency(leading.movementImpact ?? 0, companyCurrency)} in 30 days`,
      label: "Management attention",
      supporting: getForecastManagementActionSupportingCopy(leading, companyCurrency),
    };
  }

  if (leading.type === "organisational_concentration" && leading.concentration) {
    return {
      heading: `${share} of Forecast depends on ${leading.concentration.dealCount} ${leading.concentration.label} deals`,
      label: "Management attention",
    };
  }

  if (leading.type === "timing_dependency") {
    return {
      heading: `${share} of Forecast is concentrated at period end`,
      label: "Management attention",
    };
  }

  const expectedClose = formatForecastDate(leading.expectedClose);
  return {
    heading: expectedClose
      ? `${name} needs review before ${expectedClose}`
      : `${name} needs review`,
    label: "Management attention",
    supporting: getForecastManagementActionSupportingCopy(leading, companyCurrency),
  };
}

export function getForecastManagementActionCopy(
  action: ForecastManagementAction,
  companyCurrency: Currency,
) {
  if (action.type === "organisational_concentration" && action.concentration) {
    return `Review ${action.concentration.dimension.toLowerCase()} concentration — ${formatForecastShare(action.concentration.share)} of Forecast sits in ${action.concentration.label}.`;
  }

  const name = action.dealName ?? "this deal";
  const share = action.shareOfForecast === null || action.shareOfForecast === undefined
    ? null
    : formatForecastShare(action.shareOfForecast);
  const contribution = action.forecastImpact === undefined
    ? null
    : formatCompactIsoCurrency(action.forecastImpact, companyCurrency);
  const expectedClose = formatForecastDate(action.expectedClose);
  const currentRisk = action.currentRisk === null || action.currentRisk === undefined
    ? null
    : Number(action.currentRisk);

  if (action.type === "forecast_dependency") {
    const resultingGap = action.riskThesis.consequence.resultingGapToTarget;
    if (resultingGap !== undefined && resultingGap < 0) {
      return `Validate ${name} — Forecast falls ${formatCompactIsoCurrency(Math.abs(resultingGap), companyCurrency)} below target if the deal slips.`;
    }
    return `Validate ${name} — Forecast loses ${contribution} if the deal slips.`;
  }
  if (action.type === "urgent_deal") {
    const riskLevel = currentRisk === null ? null : getRiskBucket(currentRisk);
    return `Review ${name}${expectedClose ? ` before ${expectedClose}` : ""} — ${share ?? contribution} of Forecast sits in ${riskLevel ? `a ${riskLevel}-risk deal` : "this deal"}.`;
  }
  if (action.type === "worsening_outlook") {
    const names = String(action.supportingMetrics.dealNames ?? "")
      .split("|")
      .filter(Boolean);
    return `Review weakened outlook${names.length ? ` in ${joinNames(names.slice(0, 2))}` : ""} — Forecast contribution fell ${formatCompactIsoCurrency(action.movementImpact ?? 0, companyCurrency)} in 30 days.`;
  }
  if (action.type === "timing_dependency") {
    return `Review late-period dependency — ${share} of Forecast is expected to close in the final two weeks.`;
  }
  if (action.type === "forecast_assumption") {
    if (action.supportingMetrics.assumptionKind === "risk_increase") {
      return `Validate ${name} — risk increased by ${Math.round(action.riskChange ?? 0)} pts across ${share} of Forecast.`;
    }
    const signalLabel = String(action.supportingMetrics.signalLabel ?? "Forecast assumptions");
    const comparisonRatio = Number(action.supportingMetrics.comparisonRatio ?? 0);
    return `Review ${signalLabel.toLowerCase()} — the signal is ${comparisonRatio > 1 ? `${comparisonRatio.toFixed(1)}x more common in the comparison group` : "deteriorating in material Forecast deals"}.`;
  }
  return `Challenge Forecast divergence — ${contribution ?? "a material difference"} requires review.`;
}

export function getForecastManagementActionSupportingCopy(
  action: ForecastManagementAction,
  companyCurrency: Currency,
) {
  const share = formatForecastShare(action.shareOfForecast);
  const previousRisk = action.previousRisk === null || action.previousRisk === undefined
    ? null
    : Number(action.previousRisk);
  const currentRisk = action.currentRisk === null || action.currentRisk === undefined
    ? null
    : Number(action.currentRisk);
  const riskChanged = previousRisk !== null
    && currentRisk !== null
    && Number.isFinite(previousRisk)
    && Number.isFinite(currentRisk)
    && previousRisk !== currentRisk;

  if (action.type === "forecast_dependency") {
    const weakenedImpact = Number(action.supportingMetrics.largestDealImpact ?? 0);
    if (weakenedImpact > 0 && riskChanged) {
      return `${share} of Forecast · Outlook fell ${formatCompactIsoCurrency(weakenedImpact, companyCurrency)} as risk increased ${formatRiskPointChange(previousRisk, currentRisk)}.`;
    }
    if (weakenedImpact > 0) {
      return `${share} of Forecast · Outlook fell ${formatCompactIsoCurrency(weakenedImpact, companyCurrency)} in 30 days.`;
    }
    if (riskChanged) {
      return `${share} of Forecast · Risk increased ${formatRiskPointChange(previousRisk, currentRisk)}.`;
    }
    const expectedClose = formatForecastDate(action.expectedClose);
    return expectedClose
      ? `${share} of Forecast · Expected close ${expectedClose}.`
      : `${share} of Forecast.`;
  }

  if (action.type === "worsening_outlook") {
    const shareOfNegativeMovement = Number(action.supportingMetrics.shareOfNegativeMovement ?? 0);
    return shareOfNegativeMovement > 0
      ? `${formatForecastShare(shareOfNegativeMovement)} of negative movement.`
      : undefined;
  }

  if (action.type === "urgent_deal") {
    return `Forecast loses ${formatCompactIsoCurrency(action.forecastImpact ?? 0, companyCurrency)} if the deal slips.`;
  }

  if (action.type === "forecast_assumption") {
    const affectedDealCount = Number(action.supportingMetrics.affectedDealCount ?? action.dealIds.length);
    return `${formatCompactIsoCurrency(action.forecastImpact ?? 0, companyCurrency)} of Forecast across ${affectedDealCount} ${affectedDealCount === 1 ? "deal" : "deals"}.`;
  }

  return undefined;
}

export function getForecastDealEvidenceCopy<T extends ForecastInsightDeal>(
  deals: T[],
  companyCurrency: Currency,
): ForecastSectionCopy {
  const topDeal = deals.at(0) ?? null;
  return {
    heading: topDeal
      ? `Largest dependency: ${topDeal.name ?? "open deal"}`
      : "No Forecast dependencies",
    label: "KEY FORECAST DEALS",
    supporting: topDeal
      ? `${formatCompactIsoCurrency(getDealForecastContribution(topDeal), companyCurrency)}${Number.isFinite(Number(topDeal.forecast_share)) ? ` · ${formatForecastShare(Number(topDeal.forecast_share))} of Forecast` : ""}.`
      : undefined,
  };
}
